import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { RavenAlert, RavenButton, RavenCard, RavenTextField, TenantLogo } from '@ravenappdev/raven-ui';
import PnidToolTheme from '../components/product/PnidToolTheme';
import { useAuth } from '../contexts/AuthContext';
import './login.css';

export default function LoginPage() {
  const { login, register } = useAuth();
  const [mode, setMode] = useState<'login' | 'register'>('login');
  const [username, setUsername] = useState('');
  const [userGroup, setUserGroup] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      if (mode === 'login') {
        await login(username, password);
      } else {
        await register(username, userGroup, password);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  return (
    <PnidToolTheme>
      <main className="pnid-login-page">
        <div className="pnid-login-card">
          <RavenCard variant="outlined">
            <Stack spacing={3}>
              <Stack spacing={1}>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <TenantLogo tenantName="PNID Tool" size="md" onBackground="light" />
                  <Typography variant="overline" color="primary.main">PNID Tool</Typography>
                </Stack>
                <Typography variant="h4" component="h1">P&ID Flow Analyzer</Typography>
                <Typography variant="body2" color="text.secondary">
                  {mode === 'login' ? 'Sign in to continue' : 'Create an account'}
                </Typography>
              </Stack>

              <Box component="form" onSubmit={handleSubmit}>
                <Stack spacing={2}>
                  <RavenTextField
                    id="username"
                    label="Username"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    required
                    autoComplete="username"
                    fullWidth
                  />
                  {mode === 'register' && (
                    <RavenTextField
                      id="userGroup"
                      label="User Group"
                      value={userGroup}
                      onChange={(e) => setUserGroup(e.target.value)}
                      autoComplete="off"
                      fullWidth
                    />
                  )}
                  <RavenTextField
                    id="password"
                    label="Password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
                    inputProps={mode === 'register' ? { minLength: 4 } : undefined}
                    helperText={mode === 'register' ? 'Minimum 4 characters' : undefined}
                    fullWidth
                  />

                  {error && <RavenAlert severity="error">{error}</RavenAlert>}

                  <RavenButton type="submit" variant="contained" size="large" disabled={loading} fullWidth>
                    {loading ? 'Please wait…' : mode === 'login' ? 'Sign In' : 'Register'}
                  </RavenButton>
                </Stack>
              </Box>

              <Typography variant="body2" color="text.secondary">
                {mode === 'login' ? (
                  <>
                    Don&apos;t have an account?{' '}
                    <button
                      type="button"
                      className="pnid-login-switch"
                      onClick={() => { setMode('register'); setError(''); setUserGroup(''); }}
                    >
                      Register
                    </button>
                  </>
                ) : (
                  <>
                    Already have an account?{' '}
                    <button
                      type="button"
                      className="pnid-login-switch"
                      onClick={() => { setMode('login'); setError(''); }}
                    >
                      Sign In
                    </button>
                  </>
                )}
              </Typography>
            </Stack>
          </RavenCard>
        </div>
        <Box className="pnid-login-footer">
          <Typography variant="caption" color="text.secondary">by</Typography>
          <Typography variant="caption" fontWeight={700} sx={{ color: 'primary.main' }}>raven</Typography>
        </Box>
      </main>
    </PnidToolTheme>
  );
}
